export const NEWS_VERSIONS = [
  '2026-06-20',
  '2026-06-27',
  '2026-07-11',
  '2026-07-12',
  '2026-07-24',
  '2026-07-31',
  '2026-08-23',
  '2026-09-23',
] as const;

export const NEWS_VERSION = NEWS_VERSIONS[NEWS_VERSIONS.length - 1];
export const NEWS_SEEN_KEY = 'centumth_news_seen_version';

export type NewsItem = {
  id: string;
  date: string;
  dateLabel: string;
  title: string;
  summary: string;
  bullets: { label: string; text: string }[];
  footer?: string;
  isNew?: boolean;
};

export const NEWS_ITEMS: NewsItem[] = [
  {
    id: '2026-09-23',
    date: '2026-09-23',
    dateLabel: 'Sep 23, 2026',
    title: 'Ads are coming to Centumth',
    summary:
      'You’ll start seeing ads on the play screens and archive lists. The game itself isn’t changing.',
    isNew: true,
    bullets: [
      {
        label: 'Why',
        text: 'Keeping the database and the hosting running has been getting expensive. Ads are there so the site can at least pay for its own structure.',
      },
      {
        label: 'Where',
        text: 'A banner above the puzzle, side ads on a wide desktop, a closable bar at the bottom, and a couple of spots around the archive list. Nothing on top of the guess box.',
      },
      {
        label: 'Same game',
        text: 'Five guesses, the hidden Top 100, the archive write-ups. Ads sit around that, not inside it.',
      },
    ],
    footer: 'Thanks for playing. This is what keeps a new topic coming every midnight.',
  },
  {
    id: '2026-08-23',
    date: '2026-08-23',
    dateLabel: 'Aug 23, 2026',
    title: 'A better stats recap — and Full Stats',
    summary:
      'The results screen now tells a story, not just a chart. And if you want to nerd out, there’s a full stats page.',
    bullets: [
      {
        label: 'A verdict, not a spreadsheet',
        text: 'Community stats now open with a one-line take on how you played, a colored guess chart, today’s podium, the rarest find, and popular misses.',
      },
      {
        label: 'Medal mix',
        text: 'See how today’s scores broke down — bronze, silver, gold, platinum — and where you landed.',
      },
      {
        label: 'Full stats',
        text: 'Tap “Show full stats” for a deep dive: you vs the room, zone breakdowns, opening moves, and more.',
      },
    ],
    footer: 'Keep sending feedback — it directly shapes these updates.',
  },
  {
    id: '2026-07-31',
    date: '2026-07-31',
    dateLabel: 'Jul 31, 2026',
    title: 'Better lists starting August 1',
    summary:
      'We heard you: too many lists felt subjective or rested on thin sources. Starting August 1, every Top 100 gets a stricter research pass.',
    bullets: [
      {
        label: 'You called it out',
        text: 'Feedback kept coming that some rankings felt too vibes-based, or leaned on shaky / hard-to-verify sources. Fair.',
      },
      {
        label: 'More objective from Aug 1',
        text: 'New daily lists will prioritize clear methodology, named primary sources, and honest notes when a ranking has to be hybrid or approximate.',
      },
      {
        label: 'Same game, sharper answers',
        text: "The format isn't changing — just the bar for what makes the Top 100. Expect fewer \"says who?\" moments.",
      },
    ],
    footer: 'Keep sending feedback — it directly shapes these updates.',
  },
  {
    id: '2026-07-24',
    date: '2026-07-24',
    dateLabel: 'Jul 24, 2026',
    title: 'Guess distribution & Arcade upgrades',
    summary: 'See how the whole community guessed — and hunt gaps in Arcade Mode more easily.',
    bullets: [
      {
        label: 'Guess distribution',
        text: 'The results screen now shows a 100-bar chart of what players guessed. Tall bars = obvious picks; short bars = deep cuts. Your answers light up on the chart.',
      },
      {
        label: 'Arcade toggles',
        text: 'In Arcade Mode, sort found answers by rank and hide misses so the list stays clean while you hunt.',
      },
      {
        label: 'Rank strip',
        text: 'A hoverable 1–100 tick strip shows exactly which ranks you still need to find in Arcade Mode.',
      },
    ],
    footer: 'Keep sending feedback — it directly shapes these updates.',
  },
  {
    id: '2026-07-12',
    date: '2026-07-12',
    dateLabel: 'Jul 12, 2026',
    title: 'Community stats & Revamped UI',
    summary: 'You can now see how you compare to the rest of the world.',
    bullets: [
      {
        label: 'Global stats',
        text: "See today's rarest find, most guessed answers, and popular misses.",
      },
      {
        label: 'Cleaner Results',
        text: "We've reorganized the stats screen to put your results and the share button front and center.",
      },
    ],
    footer: 'Keep sending feedback — it directly shapes these updates.',
  },
  {
    id: '2026-07-11',
    date: '2026-07-11',
    dateLabel: 'Jul 11, 2026',
    title: 'Smarter matching & more personality',
    summary:
      "We've completely overhauled how answers are matched to handle typos much better, and gave the game a bit more attitude.",
    bullets: [
      {
        label: 'Typo forgiveness',
        text: 'The game now understands transposed letters, missing words, and messy spelling, automatically accepting obvious typos and suggesting the rest.',
      },
      {
        label: 'Feedback with attitude',
        text: 'The text above the input bar now reacts dynamically to your guesses. Look out for special messages when you hit certain landmark ranks!',
      },
    ],
    footer: 'Keep sending feedback — it directly shapes these updates.',
  },
  {
    id: '2026-06-27',
    date: '2026-06-27',
    dateLabel: 'Jun 27, 2026',
    title: '"Did you mean" suggestions',
    summary:
      "We heard your complaints about Centumth accepting answers you didn't mean — so now you confirm suggestions before they're counted.",
    bullets: [
      {
        label: '"Did you mean…?" suggestions',
        text: 'If your guess is close (typo/partial), Centumth will suggest matches instead of auto-accepting.',
      },
      {
        label: 'You stay in control',
        text: 'Click a suggestion to count it, or hit "No" to submit exactly what you typed.',
      },
      {
        label: 'Fewer false positives',
        text: 'This should stop random "snap" matches when you type something unrelated.',
      },
    ],
    footer: 'Keep sending feedback — it directly shapes these updates.',
  },
  {
    id: '2026-06-20',
    date: '2026-06-20',
    dateLabel: 'Jun 20, 2026',
    title: 'Arcade Mode is here',
    summary: 'A lot of you asked for a way to keep playing after your 5 official guesses, so I built it.',
    bullets: [
      {
        label: 'Arcade Mode',
        text: 'After you finish your 5 guesses, keep guessing to try to find all 100 answers.',
      },
      {
        label: 'Official score stays locked',
        text: 'Your share/stats are still based on the first 5 guesses.',
      },
      {
        label: 'Reveal flow',
        text: 'Revealing the full list locks Arcade Mode for that topic (to keep it fair).',
      },
      {
        label: 'Quality-of-life UI',
        text: 'Clearer navigation, progress, and automatic scrolling as you add guesses.',
      },
    ],
    footer: "Keep the feedback coming, it's how the game improves fastest.",
  },
];

export function getNewsUnreadCount(seenVersion: string | null): number {
  const idx = NEWS_VERSIONS.indexOf(seenVersion as (typeof NEWS_VERSIONS)[number]);
  return idx === -1 ? NEWS_VERSIONS.length : Math.max(0, NEWS_VERSIONS.length - 1 - idx);
}

export function markNewsSeen() {
  try {
    localStorage.setItem(NEWS_SEEN_KEY, NEWS_VERSION);
  } catch {
    // ignore
  }
}
